import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { emptyTableCellClassName } from '@/constants/ui'
import { formatMoscowDateTime } from '@/utils/format'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { Button } from '@/components/ui/Button'
import { SearchInput } from '@/components/ui/SearchInput'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { listClientErrors, type ClientErrorRow } from '@/api/client-errors'

const KIND_LABEL: Record<ClientErrorRow['kind'], string> = {
  boundary: 'React Boundary',
  error: 'window.onerror',
  promise: 'unhandledrejection',
}

function ClientErrorDrawer({
  row,
  onClose,
}: {
  row: ClientErrorRow
  onClose: () => void
}) {
  const { t } = useTranslation()
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-[color:var(--stryzh-overlay-scrim-modal)]"
      role="presentation"
      onClick={onClose}
    >
      <aside
        className="flex h-full w-[var(--stryzh-layout-entity-drawer-width)] max-w-full flex-col overflow-hidden border-l border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] shadow-[var(--stryzh-shadow-glow-strong)]"
        role="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[color:var(--stryzh-color-border-default)] px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)]">
          <h2 className="font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold text-[color:var(--stryzh-color-text-primary)]">
            {KIND_LABEL[row.kind]}
          </h2>
          <Button
            variant="ghost"
            onClick={onClose}
            aria-label={t('logs.clientErrors.close')}
            className="stryzh-btn--close-icon"
          >
            ✕
          </Button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)] text-sm">
          <Field label={t('logs.clientErrors.timeMsk')} value={formatMoscowDateTime(row.created_at)} />
          <Field
            label={t('logs.clientErrors.user')}
            value={row.actor_name ?? row.actor_id ?? '—'}
          />
          <Field label="URL" value={row.url ?? '—'} mono breakAll />
          <Field label={t('logs.clientErrors.version')} value={row.app_version ?? '—'} />
          <Field label="User-Agent" value={row.user_agent ?? '—'} mono breakAll />
          <Block label={t('logs.clientErrors.message')} body={row.message} />
          {row.stack ? <Block label="Stack" body={row.stack} /> : null}
          {row.component_stack ? (
            <Block label="Component stack" body={row.component_stack} />
          ) : null}
          {row.meta && Object.keys(row.meta).length > 0 ? (
            <Block label="Meta" body={JSON.stringify(row.meta, null, 2)} />
          ) : null}
        </div>
      </aside>
    </div>
  )
}

function Field({
  label,
  value,
  mono,
  breakAll,
}: {
  label: string
  value: string
  mono?: boolean
  breakAll?: boolean
}) {
  return (
    <div>
      <div className="stryzh-input-label mb-0.5">{label}</div>
      <div
        className={
          (mono ? 'font-mono text-xs ' : 'text-sm ') +
          (breakAll ? 'break-all ' : '') +
          'text-[color:var(--stryzh-color-text-primary)]'
        }
      >
        {value}
      </div>
    </div>
  )
}

function Block({ label, body }: { label: string; body: string }) {
  return (
    <div>
      <div className="stryzh-input-label mb-1">{label}</div>
      <pre className="max-h-[40dvh] overflow-auto whitespace-pre-wrap break-words rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] p-3 font-mono text-xs leading-snug text-[color:var(--stryzh-color-text-primary)]">
        {body}
      </pre>
    </div>
  )
}

export function ClientErrorsPage() {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 200)
  const [selected, setSelected] = useState<ClientErrorRow | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['client-errors'],
    queryFn: () => listClientErrors(100),
  })

  const allRows = data ?? []
  const q = debouncedSearch.toLowerCase()
  const rows = q
    ? allRows.filter(
        (r) =>
          r.message.toLowerCase().includes(q) ||
          (r.url ?? '').toLowerCase().includes(q) ||
          (r.actor_name ?? '').toLowerCase().includes(q) ||
          r.kind.includes(q),
      )
    : allRows

  return (
    <div>
      <PageHeader
        title={t('logs.clientErrors.title')}
        filters={
          <SearchInput
            placeholder={t('logs.clientErrors.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        }
      />
      {error ? (
        <div
          role="alert"
          className="mb-3 rounded-md border border-[color:var(--stryzh-color-status-error)] bg-[color:var(--stryzh-color-status-error-bg,transparent)] px-3 py-2 text-sm text-[color:var(--stryzh-color-status-error)]"
        >
          {t('logs.clientErrors.loadError', { message: (error as Error).message })}
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
            <tr>
              <th className="px-3 py-2 whitespace-nowrap">{t('logs.clientErrors.timeMsk')}</th>
              <th className="px-3 py-2">{t('logs.clientErrors.colType')}</th>
              <th className="px-3 py-2">{t('logs.clientErrors.user')}</th>
              <th className="px-3 py-2">{t('logs.clientErrors.message')}</th>
              <th className="px-3 py-2">URL</th>
              <th className="px-3 py-2 whitespace-nowrap">{t('logs.clientErrors.version')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className={emptyTableCellClassName}>
                  <Loader size="sm" centered label={t('common.loading')} />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className={emptyTableCellClassName}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className="cursor-pointer border-t border-[var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    {formatMoscowDateTime(r.created_at)}
                  </td>
                  <td className="px-3 py-2">
                    <span className="rounded border border-[var(--stryzh-color-border-default)] px-2 py-0.5 text-xs">
                      {KIND_LABEL[r.kind]}
                    </span>
                  </td>
                  <td className="px-3 py-2">{r.actor_name ?? r.actor_id ?? '—'}</td>
                  <td className="px-3 py-2 max-w-[480px] truncate" title={r.message}>
                    {r.message}
                  </td>
                  <td
                    className="px-3 py-2 max-w-[260px] truncate font-mono text-xs"
                    title={r.url ?? ''}
                  >
                    {r.url ?? '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                    {r.app_version ?? '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {selected ? (
        <ClientErrorDrawer row={selected} onClose={() => setSelected(null)} />
      ) : null}
    </div>
  )
}
